/**
 * paperMaterial — Phaser-side "paper slip" drawing for C5 "Indigo & Ink".
 *
 * The C5 doctrine (docs/design/ui-c5-indigo-ink.md) builds paper out of CSS
 * gradients + shadows. Phaser-drawn surfaces that cannot be DOM yet (dialogue,
 * journal, hearth chat, village board) get the same material here: a soft
 * cream gradient faked with a few stacked bands, a warm deckled edge, and a
 * soft drop shadow.
 *
 * Bands (rather than `fillGradientStyle`) keep the look identical across
 * renderers and cost a handful of static fills drawn once per layout.
 *
 * `paperBandColors` is pure and unit-tested; `drawPaperSlip` is the thin
 * Phaser shell around it.
 */

import type * as Phaser from 'phaser';
import {
  C5_PAPER,
  C5_PAPER_DK,
  C5_PAPER_HI,
  C5_SLIP_EDGE,
  C5_SLIP_SHADOW,
  C5_SLIP_SHADOW_ALPHA,
} from '../palette';

/** Number of gradient bands used for a small paper fill (a slip or a chip). */
export const PAPER_BANDS = 6;

/** Bands to use for a slip of height `h` — one per ~16px, so a tall sheet gets
 *  a smooth ramp instead of six visible stripes. */
export function bandsForHeight(h: number): number {
  return Math.min(28, Math.max(3, Math.round(h / 16)));
}

function lerpChannel(a: number, b: number, t: number): number {
  return Math.round(a + (b - a) * t);
}

/** Linear blend between two 0xRRGGBB colors. `t` is clamped to [0, 1]. */
export function lerpColor(from: number, to: number, t: number): number {
  const k = t < 0 ? 0 : t > 1 ? 1 : t;
  const r = lerpChannel((from >> 16) & 0xff, (to >> 16) & 0xff, k);
  const g = lerpChannel((from >> 8) & 0xff, (to >> 8) & 0xff, k);
  const b = lerpChannel(from & 0xff, to & 0xff, k);
  return (r << 16) | (g << 8) | b;
}

/**
 * Top-to-bottom band colors for a paper slip: lit cream at the top edge,
 * body cream through the middle, warm shade at the bottom fold.
 */
export function paperBandColors(steps: number = PAPER_BANDS): number[] {
  const n = Math.max(1, Math.floor(steps));
  if (n === 1) return [C5_PAPER];
  const out: number[] = [];
  for (let i = 0; i < n; i++) {
    const t = i / (n - 1);
    // First 60% of the run travels paper-hi -> paper; the tail warms to paper-dk.
    out.push(t <= 0.6
      ? lerpColor(C5_PAPER_HI, C5_PAPER, t / 0.6)
      : lerpColor(C5_PAPER, C5_PAPER_DK, (t - 0.6) / 0.4 * 0.55));
  }
  return out;
}

export interface PaperSlipOptions {
  /** Corner radius. 0 draws square-cut paper. */
  radius?: number;
  /** Body opacity — lower it when the slip sits over the painting. */
  alpha?: number;
  /** Drop-shadow offset in px. 0 disables the shadow. */
  shadow?: number;
  /** Draw the warm deckled edge stroke. */
  edge?: boolean;
  /** Number of gradient bands. Defaults to one per ~16px of height. */
  bands?: number;
}

/**
 * Draw a paper slip into `g` with its top-left at (x, y).
 * The caller owns `g` (and any clear/destroy of it).
 */
export function drawPaperSlip(
  g: Phaser.GameObjects.Graphics,
  x: number,
  y: number,
  w: number,
  h: number,
  opts: PaperSlipOptions = {},
): void {
  const radius = opts.radius ?? 3;
  const alpha = opts.alpha ?? 1;
  const shadow = opts.shadow ?? 3;
  const bands = paperBandColors(opts.bands ?? bandsForHeight(h));

  if (shadow > 0) {
    g.fillStyle(C5_SLIP_SHADOW, C5_SLIP_SHADOW_ALPHA * alpha);
    if (radius > 0) g.fillRoundedRect(x + shadow, y + shadow, w, h, radius);
    else g.fillRect(x + shadow, y + shadow, w, h);
  }

  // Base body first so the rounded silhouette is clean, then bands on top.
  g.fillStyle(C5_PAPER, alpha);
  if (radius > 0) g.fillRoundedRect(x, y, w, h, radius);
  else g.fillRect(x, y, w, h);

  const bandH = h / bands.length;
  for (let i = 0; i < bands.length; i++) {
    const top = y + i * bandH;
    // Inset the band run by the radius so it never spills past rounded corners.
    const inset = radius > 0 && (i === 0 || i === bands.length - 1) ? radius : 0;
    g.fillStyle(bands[i], alpha);
    g.fillRect(x + inset, top, w - inset * 2, bandH + 0.5);
  }

  if (opts.edge !== false) {
    g.lineStyle(1, C5_SLIP_EDGE, 0.85 * alpha);
    if (radius > 0) g.strokeRoundedRect(x, y, w, h, radius);
    else g.strokeRect(x, y, w, h);
  }
}
